<script setup lang="ts">
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { ICurrency } from '@/interfaces/ICurrency';
  import { IOption } from '@/interfaces/IOption';
  import { IOptionItem } from '@/interfaces/IOptionItem';
  import { computed } from 'vue';
  import V2ItemVariantRow from './V2ItemVariantRow.vue';

  const props = defineProps<{
    /** The item's main option (nItem.priceVariants); mutated in place. */
    priceVariants: IOption;
    currency: ICurrency;
  }>();

  const emit = defineEmits<{
    (e: 'defaultCostChanged'): void;
    (e: 'defaultDiscountedCostChanged'): void;
  }>();

  const aliveVariants = computed(() =>
    props.priceVariants.optionItems.filter((v) => !v.deletedAt)
  );

  // Stable keys for unsaved rows without polluting the API payload:
  // keyed on object identity, which survives edits and reorders.
  let nextLocalKey = -1;
  const localKeys = new WeakMap<IOptionItem, number>();
  function rowKey(variant: IOptionItem): number {
    if (variant.id) return variant.id;
    let key = localKeys.get(variant);
    if (!key) {
      key = nextLocalKey--;
      localKeys.set(variant, key);
    }
    return key;
  }

  const activeCount = computed(
    () => aliveVariants.value.filter((v) => v.active).length
  );

  function addVariant() {
    props.priceVariants.optionItems.push(<IOptionItem>{
      title: '',
      description: null,
      picture: null,
      cost: null,
      discountedCost: null,
      active: true,
    });
  }

  function removeVariant(variant: IOptionItem) {
    if (!variant.id) {
      props.priceVariants.optionItems.splice(
        props.priceVariants.optionItems.indexOf(variant),
        1
      );
    } else {
      variant.deletedAt = new Date();
    }

    if (aliveVariants.value.length === 0) addVariant();
  }

  function onCostChanged(variant: IOptionItem) {
    if (aliveVariants.value[0] === variant) emit('defaultCostChanged');
  }

  function onDiscountedCostChanged(variant: IOptionItem) {
    if (aliveVariants.value[0] === variant)
      emit('defaultDiscountedCostChanged');
  }
</script>

<template>
  <div class="item-variants-section">
    <label class="group-title-field">
      <span class="group-title-label">
        {{ $t('itemVariants.groupTitle') }}
      </span>
      <input
        v-model="priceVariants.title"
        class="group-title-input"
        type="text"
        maxlength="255"
        :placeholder="$t('itemVariants.groupTitleHint')"
      />
    </label>

    <div class="variants-list">
      <V2ItemVariantRow
        v-for="(variant, index) in aliveVariants"
        :key="rowKey(variant)"
        :variant="variant"
        :is-default="index === 0"
        :last-active="activeCount === 1"
        :currency="currency"
        @costChanged="onCostChanged(variant)"
        @discountedCostChanged="onDiscountedCostChanged(variant)"
        @remove="removeVariant(variant)"
      />
    </div>

    <PosUiButton
      color="neutral"
      variant="outline"
      size="sm"
      icon="plus"
      class="add-variant-button"
      @click="addVariant"
    >
      {{ $t('itemVariants.addVariant') }}
    </PosUiButton>
  </div>
</template>

<style scoped lang="scss">
  .item-variants-section {
    @apply tw-flex tw-flex-col tw-gap-3;

    .group-title-field {
      @apply tw-flex tw-flex-col tw-gap-1;

      .group-title-label {
        @apply tw-text-xs tw-font-medium tw-text-neutral-500;
      }

      .group-title-input {
        @apply tw-h-11 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

        &:focus {
          @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
        }
      }
    }

    .variants-list {
      @apply tw-flex tw-flex-col tw-gap-2;
    }

    .add-variant-button {
      @apply tw-self-start;
    }
  }
</style>
